import { prefersReducedMotion } from '@lucid/core';

/* Snap rail with previous/next buttons. Buttons reflect the scroll position and step by one view. */
class LucidRail extends HTMLElement {
  #frame = 0;
  #resizeObserver = null;

  connectedCallback() {
    this.track = this.querySelector('[data-lucid-rail-track]');
    this.prev = this.querySelector('[data-lucid-rail-prev]');
    this.next = this.querySelector('[data-lucid-rail-next]');
    if (!this.track) return;

    this.onScroll = () => this.#schedule();
    this.onPrev = () => this.#step(-1);
    this.onNext = () => this.#step(1);

    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.prev?.addEventListener('click', this.onPrev);
    this.next?.addEventListener('click', this.onNext);
    this.#resizeObserver = new ResizeObserver(() => this.#schedule());
    this.#resizeObserver.observe(this.track);
    this.#sync();
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    this.prev?.removeEventListener('click', this.onPrev);
    this.next?.removeEventListener('click', this.onNext);
    this.#resizeObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  get #direction() {
    return getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
  }

  #step(sign) {
    const items = Array.from(this.track.children);
    const first = items[0];
    if (!first) return;
    const gap = Number.parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const itemWidth = first.getBoundingClientRect().width + gap;
    const perView = Math.max(1, Math.floor((this.track.clientWidth + gap) / itemWidth));
    this.track.scrollBy({
      left: sign * this.#direction * perView * itemWidth,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }

  #schedule() {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#sync();
    });
  }

  #sync() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const position = Math.abs(scrollLeft);
    const max = scrollWidth - clientWidth;
    const atStart = position <= 2;
    const atEnd = position >= max - 2;
    this.toggleAttribute('data-scrollable', max > 2);
    if (this.prev) this.prev.disabled = atStart;
    if (this.next) this.next.disabled = atEnd;
  }
}

if (!customElements.get('lucid-rail')) customElements.define('lucid-rail', LucidRail);
